//index.jsx
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import NavigationBar from '../../components/ui/NavigationBar';
import ServicesBreadcrumb from '../../components/ui/ServicesBreadcrumb';
import Button from '../../components/ui/Button';
import { Checkbox } from '../../components/ui/Checkbox';
import Icon from '../../components/AppIcon';

// Import all components
import FormProgressIndicator from './components/FormProgressIndicator';
import ClientInformationSection from './components/ClientInformationSection';
import ServiceSelectionSection from './components/ServiceSelectionSection';
import ProjectDetailsSection from './components/ProjectDetailsSection';
import FileUploadSection from './components/FileUploadSection';
import ServiceSummaryCard from './components/ServiceSummaryCard';
import TrustSignalsCard from './components/TrustSignalsCard';
import SuccessMessage from './components/SuccessMessage';
import { index } from 'd3';

const ApplicationFormPage = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [applicationId, setApplicationId] = useState('');
  const [errors, setErrors] = useState({});

  const steps = ['Client Info', 'Services', 'Project Details', 'Documents', 'Review'];
  const totalSteps = steps?.length;

  const [formData, setFormData] = useState({
    // Client Information
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    companyName: '',
    jobTitle: '',
    website: '',
    
    // Service Selection
    primaryService: '',
    additionalServices: [],
    
    // Project Details
    projectTitle: '',
    projectDescription: '',
    budget: '',
    timeline: '',
    additionalRequirements: '',
    
    // Files and Agreement
    uploadedFiles: [],
    agreeToTerms: false,
    subscribeNewsletter: false
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentStep]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors?.[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleServiceChange = (serviceId, isChecked) => {
    setFormData(prev => ({
      ...prev,
      additionalServices: isChecked
        ? [...prev?.additionalServices, serviceId]
        : prev?.additionalServices?.filter(id => id !== serviceId)
    }));
  };

  const handleFileUpload = (file) => {
    if (file?.size > 10 * 1024 * 1024) { // 10MB limit
      alert('File size must be less than 10MB');
      return;
    }

    setFormData(prev => ({
      ...prev,
      uploadedFiles: [...prev?.uploadedFiles, file]
    }));
  };

  const handleFileRemove = (index) => {
    setFormData(prev => ({
      ...prev,
      uploadedFiles: prev?.uploadedFiles?.filter((_, i) => i !== index)
    }));
  };

  const validateStep = (step) => {
    const newErrors = {};

    switch (step) {
      case 1: // Client Information
        if (!formData?.firstName?.trim()) newErrors.firstName = 'First name is required';
        if (!formData?.lastName?.trim()) newErrors.lastName = 'Last name is required';
        if (!formData?.email?.trim()) {
          newErrors.email = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
          newErrors.email = 'Please enter a valid email address';
        }
        if (!formData?.phone?.trim()) newErrors.phone = 'Phone number is required';
        if (!formData?.companyName?.trim()) newErrors.companyName = 'Company name is required';
        break;

      case 2: // Service Selection
        if (!formData?.primaryService) newErrors.primaryService = 'Please select a primary service';
        break;

      case 3: // Project Details
        if (!formData?.projectTitle?.trim()) newErrors.projectTitle = 'Project title is required';
        if (!formData?.projectDescription?.trim()) {
          newErrors.projectDescription = 'Project description is required';
        } else if (formData?.projectDescription?.trim()?.length < 50) {
          newErrors.projectDescription = 'Project description must be at least 50 characters';
        }
        if (!formData?.budget) newErrors.budget = 'Please select a budget range';
        if (!formData?.timeline) newErrors.timeline = 'Please select a timeline';
        break;

      case 5: // Review & Submit
        if (!formData?.agreeToTerms) newErrors.agreeToTerms = 'You must agree to the terms and conditions';
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, totalSteps));
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) return;

    setIsSubmitting(true);
    
    // Simulate API call
    try {
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Generate application ID
      const id = 'APP-' + Date.now()?.toString()?.slice(-6);
      setApplicationId(id);
      setIsSubmitted(true);
    } catch (error) {
      console.error('Submission error:', error);
      alert('There was an error submitting your application. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStartNew = () => {
    setIsSubmitted(false);
    setCurrentStep(1);
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      companyName: '',
      jobTitle: '',
      website: '',
      primaryService: '',
      additionalServices: [],
      projectTitle: '',
      projectDescription: '',
      budget: '',
      timeline: '',
      additionalRequirements: '',
      uploadedFiles: [],
      agreeToTerms: false,
      subscribeNewsletter: false
    });
    setErrors({});
    setApplicationId('');
  };

  if (isSubmitted) {
    return (
      <SuccessMessage 
        applicationId={applicationId}
        onStartNew={handleStartNew}
      />
    );
  }

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <ClientInformationSection
            formData={formData}
            handleInputChange={handleInputChange}
            errors={errors}
          />
        );
      case 2:
        return (
          <ServiceSelectionSection
            formData={formData}
            handleInputChange={handleInputChange}
            handleServiceChange={handleServiceChange}
            errors={errors}
          />
        );
      case 3:
        return (
          <ProjectDetailsSection
            formData={formData}
            handleInputChange={handleInputChange}
            errors={errors}
          />
        );
      case 4:
        return (
          <FileUploadSection
            formData={formData}
            handleFileUpload={handleFileUpload}
            handleFileRemove={handleFileRemove}
          />
        );
      case 5:
        return (
          <div className="space-y-6">
            <div className="border-b border-border pb-4">
              <h3 className="text-xl font-semibold text-foreground mb-2">Review & Submit</h3>
              <p className="text-text-secondary text-sm">
                Please review your application details before submitting
              </p>
            </div>
            {/* Application Summary */}
            <div className="bg-card border border-border rounded-lg p-6">
              <h4 className="font-semibold text-card-foreground mb-4">Application Summary</h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
                <div>
                  <h5 className="font-medium text-card-foreground mb-2">Client Information</h5>
                  <div className="space-y-1 text-muted-foreground">
                    <p><span className="font-medium">Name:</span> {formData?.firstName} {formData?.lastName}</p>
                    <p><span className="font-medium">Email:</span> {formData?.email}</p>
                    <p><span className="font-medium">Phone:</span> {formData?.phone}</p>
                    <p><span className="font-medium">Company:</span> {formData?.companyName}</p>
                  </div>
                </div>
                
                <div>
                  <h5 className="font-medium text-card-foreground mb-2">Project Details</h5>
                  <div className="space-y-1 text-muted-foreground">
                    <p><span className="font-medium">Title:</span> {formData?.projectTitle}</p>
                    <p><span className="font-medium">Budget:</span> {formData?.budget}</p>
                    <p><span className="font-medium">Timeline:</span> {formData?.timeline}</p>
                    <p><span className="font-medium">Files:</span> {formData?.uploadedFiles?.length} uploaded</p>
                  </div>
                </div>
              </div>
            </div>
            {/* Terms and Conditions */}
            <div className="space-y-4">
              <Checkbox
                label="I agree to the Terms and Conditions and Privacy Policy"
                checked={formData?.agreeToTerms}
                onChange={handleInputChange}
                name="agreeToTerms"
                error={errors?.agreeToTerms}
                required
              />
              
              <Checkbox
                label="Subscribe to our newsletter for updates and tech insights"
                checked={formData?.subscribeNewsletter}
                onChange={handleInputChange}
                name="subscribeNewsletter"
              />
            </div>
            {/* Important Notice */}
            <div className="bg-accent/10 border border-accent/20 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <Icon name="Info" size={20} className="text-accent flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-card-foreground mb-1">Important Notice</p>
                  <p className="text-muted-foreground">
                    By submitting this application, you acknowledge that all information provided is accurate and complete. 
                    Our team will review your submission and contact you within 24-48 hours to discuss next steps.
                  </p>
                </div>
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <NavigationBar />
      <ServicesBreadcrumb 
        serviceCategory="Services" 
        serviceName="Application Form" 
        className="mt-16 lg:mt-20" 
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Main Form Content */}
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-card border border-border rounded-lg p-6 lg:p-8"
            >
              <FormProgressIndicator
                currentStep={currentStep}
                totalSteps={totalSteps}
                steps={steps}
              />

              <motion.div
                key={currentStep}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                {renderStepContent()}
              </motion.div>

              {/* Navigation Buttons */}
              <div className="flex justify-between items-center mt-8 pt-6 border-t border-border">
                <Button
                  variant="outline"
                  onClick={handlePrevious}
                  disabled={currentStep === 1}
                  iconName="ChevronLeft"
                  iconPosition="left"
                >
                  Previous
                </Button>

                <div className="flex space-x-3">
                  {currentStep < totalSteps ? (
                    <Button
                      variant="default"
                      onClick={handleNext}
                      iconName="ChevronRight"
                      iconPosition="right"
                    >
                      Next Step
                    </Button>
                  ) : (
                    <Button
                      variant="default"
                      onClick={handleSubmit}
                      loading={isSubmitting}
                      iconName="Send"
                      iconPosition="left"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Submitting...' : 'Submit Application'}
                    </Button>
                  )}
                </div>
              </div>
            </motion.div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-5 space-y-6">
            <ServiceSummaryCard formData={formData} />
            <TrustSignalsCard />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApplicationFormPage;